import React, { useCallback, useState, useMemo } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogTrigger, DialogPortal } from '@frontend/shadcn/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Input } from '@frontend/shadcn/components/ui/input';
import DraggableList, { ListItem } from '@frontend/app/refresh-components/DraggableList';
import { useMessagingContext } from '@frontend/hooks';
import clsx from 'clsx';
import { CheckedState } from '@radix-ui/react-checkbox';
import { cn } from '@frontend/shadcn/lib/utils';
import { AdsSocialColumn } from './hooks/use-ads-column-dialog';

// Custom DialogOverlay with z-index 200
const CustomDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[200] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
CustomDialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Custom DialogContent with z-index 250
const CustomDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <CustomDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-[250] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
CustomDialogContent.displayName = DialogPrimitive.Content.displayName;

const renderSubColumns = (subColumns?: AdsSocialColumn[], depth: number = 0) => {
  if (!subColumns?.length) return null;

  return (
    <ul className={clsx(depth > 0 ? 'pl-5 list-disc' : '')}>
      {subColumns.map((subColumn) => (
        <li key={subColumn.id} className="text-xs text-gray-800">
          <div>{subColumn.header}</div>
          {renderSubColumns(subColumn.subColumns, depth + 1)}
        </li>
      ))}
    </ul>
  );
};

const columnToListItem = <T extends AdsSocialColumn>(column: T): ListItem<T> => {
  return {
    id: column.id,
    content: (
      <div className="flex flex-col gap-2">
        <div className="text-gray-600 text-sm">{column.header}</div>
        {renderSubColumns(column.subColumns)}
      </div>
    ),
    data: column,
    isFixed: column.isFixed,
    isRemovable: !column.isFixed,
  };
};

interface AdsColumnDialogProps<T extends AdsSocialColumn = AdsSocialColumn> {
  defaultSelectedColumns: T[];
  selectedColumns: T[];
  setSelectedColumns: React.Dispatch<React.SetStateAction<T[]>>;
  handleDragEndModalColumn: (items: ListItem<T>[], action?: 'removeItem' | 'dragItem') => void;
  onToggleColumnCheckBox: (id: string) => void;
  onSave: (columns: T[]) => Promise<void>;
  onToggleSelectall: (checked: CheckedState) => void;
  children?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const renderColumn = (
  column: AdsSocialColumn,
  onToggleColumnCheckBox: (id: string) => void,
  depth: number = 1,
  isParentChecked: boolean = true,
) => {
  const selectedSubColumnsCount = column.subColumns?.filter((subColumn) => !subColumn.hidden).length ?? 0;
  const isColumnChecked = !column.hidden && isParentChecked;
  const isDisabled = !isParentChecked;

  return (
    <li key={column.id} className="space-y-1">
      <label className={`flex items-center space-x-2 ${isDisabled ? 'text-gray-400' : ''}`}>
        <Checkbox
          checked={isColumnChecked}
          onCheckedChange={() => onToggleColumnCheckBox(column.id)}
          className="rounded border-gray-300"
          disabled={isDisabled || column.isFixed}
          data-dd-action-name={`toggle-column-${column.header}`}
          aria-label={`Toggle ${column.header} column`}
        />
        <span>
          {column.header}
          {column.subColumns && column.subColumns.length > 0 && ` (${selectedSubColumnsCount})`}
        </span>
      </label>

      {(column.subColumns ?? []).length > 0 && (
        <ul className={`ml-${depth * 2} space-y-2`}>
          {(column.subColumns ?? []).map((subColumn) =>
            renderColumn(subColumn, onToggleColumnCheckBox, depth + 1, isColumnChecked),
          )}
        </ul>
      )}
    </li>
  );
};

export const AdsColumnDialog = <T extends AdsSocialColumn = AdsSocialColumn>({
  defaultSelectedColumns: _defaultSelectedColumns,
  onToggleSelectall,
  selectedColumns,
  setSelectedColumns: _setSelectedColumns,
  handleDragEndModalColumn,
  onToggleColumnCheckBox,
  onSave,
  children,
  isOpen: controlledOpen,
  onOpenChange,
}: AdsColumnDialogProps<T>) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { showErrorMessage } = useMessagingContext();

  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = typeof controlledOpen === 'boolean' ? controlledOpen : internalOpen;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (typeof controlledOpen === 'boolean') {
        if (onOpenChange) {
          onOpenChange(open);
        } else {
          console.warn(
            'AdsColumnDialog: `isOpen` prop provided without `onOpenChange`. Dialog will not close as expected.',
          );
        }
      } else {
        setInternalOpen(open);
      }
    },
    [controlledOpen, onOpenChange],
  );

  const areAllColumnsVisible = selectedColumns.every((col) => !col.hidden);
  const areAnyColumnsVisible = selectedColumns.some((col) => !col.hidden && !col.isFixed);

  const filteredColumns = selectedColumns.filter(
    (column) =>
      column.header.toLowerCase().includes(searchQuery.toLowerCase()) ||
      column.subColumns?.some((subColumn) => subColumn.header.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const saveColumns = useCallback(async () => {
    setIsSaving(true);

    try {
      await onSave(selectedColumns);
      handleOpenChange(false);
    } catch (error) {
      console.error('Failed to save columns:', error);
      showErrorMessage('Failed to save column settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [onSave, selectedColumns, handleOpenChange, showErrorMessage]);

  const leftColumns = useMemo(() => {
    return filteredColumns
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((column) => renderColumn(column, onToggleColumnCheckBox));
  }, [filteredColumns, onToggleColumnCheckBox]);

  const selectAllState = areAllColumnsVisible ? true : areAnyColumnsVisible;

  const totalSelectedColumns = useMemo(() => {
    const countSelectedColumns = (columns: AdsSocialColumn[]): number => {
      return columns.reduce((count, column) => {
        const selectedSubColumnsCount = column.subColumns ? countSelectedColumns(column.subColumns) : 0;
        return count + (!column.hidden ? 1 : 0) + selectedSubColumnsCount;
      }, 0);
    };

    return countSelectedColumns(selectedColumns);
  }, [selectedColumns]);

  const selectAllLabel = `${selectAllState ? 'Unselect All' : 'Select All'} (${totalSelectedColumns})`;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        handleOpenChange(open);
      }}
    >
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <CustomDialogContent className="bg-white flex flex-col w-[600px] max-w-full max-h-full z-[250]">
        <DialogHeader className="border-b pb-2">
          <DialogTitle>Edit Columns</DialogTitle>
        </DialogHeader>
        <div className="px-6">
          <div>Your changes will apply to this table for all users.</div>
          <div className="grid grid-cols-2 gap-4 pt-4 flex-grow">
            {/* Left Column */}
            <div className="pr-4">
              <h3 className="text-sm font-medium mb-2">Add Columns</h3>

              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border rounded-md px-3 py-2 mb-2"
                data-dd-action-name="search-columns"
                aria-label="Search columns"
              />
              <div className="pr-4 max-h-[344px] overflow-y-auto">
                <ul className="space-y-2">
                  <li key="selectAll" className="space-y-1">
                    <label className="flex items-center space-x-2">
                      <Checkbox
                        checked={selectAllState}
                        onCheckedChange={(checked) => onToggleSelectall(!!checked)}
                        className="rounded border-gray-300"
                        data-dd-action-name="toggle-all-columns"
                        aria-label={selectAllLabel}
                      />
                      <span>{selectAllLabel}</span>
                    </label>
                  </li>
                  {leftColumns}
                </ul>
              </div>
            </div>

            {/* Right Column with Draggable List */}
            <div className="border-l pl-4">
              <h3 className="text-sm font-medium mb-2">Column Order</h3>
              <div className="max-h-[344px] overflow-y-auto">
                <DraggableList
                  initialItems={filteredColumns.map(columnToListItem)}
                  onItemsChange={handleDragEndModalColumn}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
            data-dd-action-name="cancel-column-dialog"
          >
            Cancel
          </Button>
          <Button onClick={saveColumns} disabled={isSaving} data-dd-action-name="save-column-dialog">
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CustomDialogContent>
    </Dialog>
  );
};
